// shelbulaworkspaces/FileTree.jsx
import React from 'react';
import { File, ChevronRight, ChevronDown, AlertTriangle, Loader, Pin } from 'lucide-react';

const WORKSPACE_BRAIN_NAME = '.WorkspaceBrain';

const isWorkspaceBrainNode = (node) => node?.path === WORKSPACE_BRAIN_NAME || node?.name === WORKSPACE_BRAIN_NAME;

/* ========================
 * formatTimeAgo - Formats a date string into a relative time string
 * ========================*/
const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        const now = new Date();
        const seconds = Math.round((now.getTime() - date.getTime()) / 1000);
        
        if (seconds < 60) return `${seconds}s ago`;
        const minutes = Math.round(seconds / 60);
        if (minutes < 60) return `${minutes}m ago`;
        const hours = Math.round(minutes / 60);
        if (hours < 24) return `${hours}h ago`;
        const days = Math.round(hours / 24);
        return `${days}d ago`;
    } catch (e) {
        return '';
    }
};

/* ========================
 * formatFileSize - Formats bytes into a human-readable string
 * ========================*/
const formatFileSize = (bytes) => {
    if (bytes === null || bytes === undefined) return '';
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};


/* ========================
 * TreeNode - Renders a single node (file or folder) in the tree
 * ========================*/
const TreeNode = ({ node, level, ...props }) => {
    const { onOpenFile, expandedFolders, onToggleFolder, onContextMenu, showFileMetadata = true, activeFilePath = null, pinnedFilePaths = new Set(), isBrainEnabled = false } = props;
    
    const isDirectory = node.type === 'directory';
    const isExpanded = expandedFolders.has(node.path);
    const isConflicted = node.gitStatus === 'conflicted';
    const isLoading = node.isLoading;
    const isWorkspaceBrain = level === 0 && isWorkspaceBrainNode(node);
    const isPinnedWorkspaceFile = node.type === 'file' && pinnedFilePaths.has(node.path);
    const isWorkspaceBrainActive = isWorkspaceBrain && isBrainEnabled;
    const isSkillsFolder = isDirectory && node.path === `${WORKSPACE_BRAIN_NAME}/skills`;
    const isInsideBrainFolder = !isWorkspaceBrain && isBrainEnabled && String(node.path || '').startsWith(`${WORKSPACE_BRAIN_NAME}/`);
    const indent = { paddingLeft: `${level * 16}px` };

    const handleNodeClick = (e) => {
        e.stopPropagation();
        if (isDirectory) {
            onToggleFolder(node);
        } else {
            onOpenFile(node);
        }
    };
    
    const handleContextMenu = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onContextMenu) {
            onContextMenu(e.clientX, e.clientY, node);
        }
    };

    const renderIcon = () => {
        if (isLoading) {
            return <Loader size={16} className="mr-1 flex-shrink-0 animate-spin text-on-surface-dim" />;
        }
        if (isDirectory) {
            return isExpanded ? <ChevronDown size={16} className="mr-1 flex-shrink-0" /> : <ChevronRight size={16} className="mr-1 flex-shrink-0" />;
        }
        return <File size={16} className="mr-1 flex-shrink-0 text-on-surface-dim" />;
    };

    const rowClassName = isWorkspaceBrain
        ? 'sticky top-0 z-10'
        : `hover:bg-surface-dark ${isConflicted ? 'bg-orange-500/10 hover:bg-orange-500/20' : ''}`;

    const brainRowStyle = (isWorkspaceBrain || isInsideBrainFolder) ? {
        backgroundColor: isWorkspaceBrainActive || isInsideBrainFolder
            ? 'rgba(var(--primary-rgb), 0.15)'
            : 'rgba(var(--on-surface-rgb), 0.06)',
    } : null;

    const titleClassName = isWorkspaceBrain
        ? (isWorkspaceBrainActive ? 'text-primary' : 'text-on-surface-dim')
        : `${isConflicted ? 'text-orange-300' : ''}`;

    return (
        <div>
            <div
                onClick={handleNodeClick}
                onContextMenu={onContextMenu ? handleContextMenu : undefined}
                className={isWorkspaceBrain
                    ? `flex items-center px-2 text-sm cursor-pointer transition-colors ${rowClassName}`
                    : isInsideBrainFolder
                    ? `flex items-center px-2 py-1.5 text-sm cursor-pointer transition-colors ${rowClassName}`
                    : `flex items-center px-2 py-1.5 mx-2 text-sm rounded-md cursor-pointer transition-colors ${rowClassName}`}
                style={isWorkspaceBrain
                    ? { paddingTop: '0.33rem', paddingBottom: '0.33rem', ...brainRowStyle }
                    : { ...indent, ...brainRowStyle }}
            >
                <div className={`flex-grow flex items-center min-w-0 ${isSkillsFolder ? 'gap-1.5' : 'justify-between'}`}>
                    {/* Left part: icon and name */}
                    <div className="flex items-center truncate min-w-0">
                        {renderIcon()}
                        <div className="flex items-center gap-1.5 min-w-0 truncate">
                                <span className={`truncate ${titleClassName}`}>{node.name}</span>
                                {isPinnedWorkspaceFile ? <Pin size={11} className="flex-shrink-0" style={{ color: 'rgb(var(--primary-rgb))' }} /> : null}
                            </div>
                         {isConflicted && <AlertTriangle size={14} className="ml-2 flex-shrink-0 text-orange-400" title="This file has merge conflicts." />}
                    </div>

                    {/* ? help icon inline next to skills folder name */}
                    {isSkillsFolder && (
                        <div className="relative group/skillhelp flex-shrink-0" onClick={e => e.stopPropagation()}>
                            <span
                                className="flex items-center justify-center rounded-full cursor-default select-none"
                                style={{ width: 14, height: 14, border: '1px solid rgba(var(--primary-rgb), 0.4)', color: 'rgba(var(--primary-rgb), 0.55)', fontSize: 9, lineHeight: 1 }}
                            >?</span>
                            <div
                                className="absolute left-0 top-5 z-[200] invisible group-hover/skillhelp:visible pointer-events-none rounded-lg"
                                style={{ width: 200, padding: '7px 10px', backgroundColor: 'rgba(15, 15, 25, 0.97)', border: '1px solid rgba(var(--primary-rgb), 0.18)', color: 'rgba(var(--on-surface-rgb), 0.7)', fontSize: 10, lineHeight: 1.55, whiteSpace: 'normal' }}
                            >
                                Ask your agent to create a skill to use in this workspace. You can also drop your own skill.md files or folders.
                            </div>
                        </div>
                    )}

                    {/* Right part: ACTIVE label for brain row, metadata for files */}
                    {isWorkspaceBrain ? (
                        isWorkspaceBrainActive ? (
                            <span className="flex-shrink-0 text-[9px] tracking-[0.2em] uppercase ml-2" style={{ color: 'rgba(var(--primary-rgb), 0.5)' }}>Active</span>
                        ) : null
                    ) : !isSkillsFolder && !isDirectory && node.lastModified && showFileMetadata ? (
                        <div className="flex items-center text-xs text-on-surface-dim ml-2 flex-shrink-0">
                            <span className="w-16 text-right">{formatFileSize(node.size)}</span>
                            <span className="w-20 text-right">{formatTimeAgo(node.lastModified)}</span>
                        </div>
                    ) : null}
                </div>
            </div>
            {isExpanded && isDirectory && node.children && (
                <Tree nodes={node.children} level={level + 1} {...props} insideBrain={props.insideBrain || isWorkspaceBrain} />
            )}
        </div>
    );
};

/* ========================
 * Tree - Renders a collection of TreeNodes
 * ========================*/
export const Tree = ({ nodes, level, ...props }) => {
    const { sortOrder, showHiddenFiles } = props;
    const rawNodes = Object.values(nodes);
    const workspaceBrainNode = level === 0
        ? rawNodes.find((node) => isWorkspaceBrainNode(node))
        : null;

    // Nodes inside .WorkspaceBrain are always visible regardless of the hidden-files toggle.
    const isInsideBrain = level > 0 && (props.insideBrain || false);

    const visibleNodes = rawNodes
        .filter((node) => node.path !== workspaceBrainNode?.path)
        .filter(node => isInsideBrain || showHiddenFiles || !node.name.startsWith('.'));

    const sortedNodes = visibleNodes.sort((a, b) => {
        if (sortOrder === 'time') {
            const aIsDirectory = a.type === 'directory';
            const bIsDirectory = b.type === 'directory';

            if (aIsDirectory !== bIsDirectory) {
                return aIsDirectory ? -1 : 1; // Directories first
            }

            if (!aIsDirectory) { // Both are files
                const aTime = a.lastModified ? new Date(a.lastModified).getTime() : 0;
                const bTime = b.lastModified ? new Date(b.lastModified).getTime() : 0;
                if (bTime !== aTime) {
                    return bTime - aTime; // Newest first
                }
            }
            
            // For directories, or files with same time, sort alphabetically
            return a.name.localeCompare(b.name);
        }
        
        // Default sort: directories first, then alphabetical
        if (a.type === b.type) return a.name.localeCompare(b.name);
        return a.type === 'directory' ? -1 : 1;
    });

    const orderedNodes = workspaceBrainNode
        ? [workspaceBrainNode, ...sortedNodes]
        : sortedNodes;

    return (
        <div className={level === 0 ? 'pb-2' : ''}>
            {orderedNodes.map(node => (
                <TreeNode key={node.path} node={node} level={level} {...props} />
            ))}
        </div>
    );
};